import DateUtils from "../../../shared/utils/DateUtils";

export const normalizeChartDate = date => {
	if (!date) return null;
	if (typeof date === "number") return date;

	const timestamp = DateUtils.parseDateOnlyToTimestamp(date);
	return timestamp || null;
};

export const getMiddleDate = (start, end) => start + (end - start) / 2;

export const getGoalMinimumValue = goal => {
	return goal?.minimumValue ?? 0;
};

export const getGoalDiscountPercentage = goal => {
	return goal?.discountPercentage ?? 0;
};

export const sortGoalsByMinimumValue = goals => {
	return [...goals].sort((firstGoal, secondGoal) => (
		Number(getGoalMinimumValue(firstGoal) || 0) -
		Number(getGoalMinimumValue(secondGoal) || 0)
	));
};

export const getGoalValues = goals => {
	return goals.map(goal => Number(getGoalMinimumValue(goal) || 0));
};

export const getDataMaxValue = (data = [], dataKeys = []) => {
	return data.reduce((maxValue, item) => {
		const itemMaxValue = dataKeys.reduce((currentMaxValue, dataKey) => {
			const value = Number(item?.[dataKey] || 0);

			return Math.max(currentMaxValue, value);
		}, 0);

		return Math.max(maxValue, itemMaxValue);
	}, 0);
};

export const getGoalBasedChartMaxValue = ({
	baseline = 0,
	goalValues = [],
	dataMaxValue = 0,
	roundBase = 1000,
	referenceMultiplier = 1.25,
	dataLimitMultiplier = 1.8
}) => {
	const referenceMaxValue = Math.max(
		Number(baseline || 0),
		...goalValues,
		0
	);

	const referenceLimit = referenceMaxValue * referenceMultiplier;
	const dataLimit = referenceMaxValue * dataLimitMultiplier;

	const maxValue = dataMaxValue > dataLimit
		? referenceLimit
		: Math.max(referenceLimit, dataMaxValue);

	return Math.ceil(maxValue / roundBase) * roundBase;
};

export const clampChartDataValues = ({
	data = [],
	dataKeys = [],
	maxValue
}) => {
	return data.map(item => {
		const nextItem = { ...item };

		dataKeys.forEach(dataKey => {
			const value = Number(item?.[dataKey] || 0);

			nextItem[`${dataKey}Label`] = value;

			if (value > maxValue) {
				nextItem[`${dataKey}Original`] = value;
				nextItem[dataKey] = maxValue;
				nextItem.isOutOfScale = true;
			}
		});

		return nextItem;
	});
};

export const clampDailySalesChartDataValues = ({
	data = [],
	maxValue
}) => {
	const chartMaxValue = Number(maxValue || 0);
	const projectedVisualMaxValue = chartMaxValue * 0.90;
	const currentDailySalesVisualMaxValue = chartMaxValue;

	return data.map(item => {
		const nextItem = { ...item };

		const dailyAverage = Number(item?.dailyAverage || 0);
		const currentDailySales = Number(item?.currentDailySales || 0);

		if (dailyAverage > 0) {
			nextItem.dailyAverageLabel = dailyAverage;

			if (dailyAverage > projectedVisualMaxValue) {
				nextItem.dailyAverageOriginal = dailyAverage;
				nextItem.dailyAverage = projectedVisualMaxValue;
				nextItem.isOutOfScale = true;
			}
		}

		if (currentDailySales > 0) {
			nextItem.currentDailySalesLabel = currentDailySales;

			if (currentDailySales > currentDailySalesVisualMaxValue) {
				nextItem.currentDailySalesOriginal = currentDailySales;
				nextItem.currentDailySales = currentDailySalesVisualMaxValue;
				nextItem.isOutOfScale = true;
			}
		}

		return nextItem;
	});
};

export const getOriginalValue = (payload, dataKey, fallbackValue) => {
	return (
		payload?.[`${dataKey}Label`] ??
		payload?.[`${dataKey}Original`] ??
		fallbackValue
	);
};

export const getCompactChartLayout = ({
	config,
	data = [],
	referenceLines = [],
	minGap = 24
}) => {
	const {
		height = 600,
		margin = {},
		yAxisLeft,
		yAxisRight
	} = config;

	const fallback = {
		height,
		data,
		referenceLines,
		areas: config.areas,
		yAxisLeft,
		yAxisRight
	};

	const isNumeric = value => (
		value !== null &&
		value !== undefined &&
		value !== "" &&
		Number.isFinite(Number(value))
	);

	const [min, max] = yAxisLeft.domain.map(Number);

	// Desconta margens e a altura padrão do eixo horizontal.
	const plotHeight =
		height -
		(margin.top ?? 0) -
		(margin.bottom ?? 0) -
		30;

	if (
		!Number.isFinite(min) ||
		!Number.isFinite(max) ||
		max <= min ||
		plotHeight <= 0
	) {
		return fallback;
	}

	// Os valores das referências delimitam as faixas.
	const anchors = [
		...new Set([
			min,
			max,
			...referenceLines
				.filter(line => (
					isNumeric(line.y) &&
					(line.label || line.rightLabel)
				))
				.map(line => Number(line.y))
				.filter(value => value >= min && value <= max)
		])
	].sort((first, second) => first - second);

	const gap = Math.min(
		minGap / plotHeight,
		1 / (anchors.length - 1)
	);

	const remaining = 1 - gap * (anchors.length - 1);

	const positions = anchors.map((value, index) => {
		if (index === 0) return min;
		if (index === anchors.length - 1) return max;

		return min + (max - min) * (
			index * gap +
			remaining * (value - min) / (max - min)
		);
	});

	const mapValue = value => {
		if (!isNumeric(value)) return value;

		const number = Number(value);

		let index = anchors.findIndex(anchor => anchor >= number);

		if (index < 0) {
			index = anchors.length - 1;
		}

		index = Math.max(1, index);

		const ratio = (
			(number - anchors[index - 1]) /
			(anchors[index] - anchors[index - 1])
		);

		return positions[index - 1] + ratio * (
			positions[index] - positions[index - 1]
		);
	};

	const mapAxis = axis => (
		axis
			? {
				...axis,
				ticks: axis.ticks?.map(mapValue)
			}
			: axis
	);

	return {
		height,

		data: data.map(item => {
			const nextItem = { ...item };

			config.series.forEach(({ dataKey }) => {
				if (!isNumeric(item[dataKey])) {
					return;
				}

				// Preserva o valor real nos rótulos e tooltips.
				nextItem[`${dataKey}Label`] = getOriginalValue(
					item,
					dataKey,
					Number(item[dataKey])
				);

				nextItem[dataKey] = mapValue(item[dataKey]);
			});

			return nextItem;
		}),

		referenceLines: referenceLines.map(line => ({
			...line,
			y: mapValue(line.y),
			labelVerticalOffset: 0,
			rightLabelVerticalOffset: 0
		})),

		areas: (config.areas || []).map(area => ({
			...area,
			y1: mapValue(area.y1),
			y2: mapValue(area.y2)
		})),

		yAxisLeft: mapAxis(yAxisLeft),
		yAxisRight: mapAxis(yAxisRight)
	};
};